import * as React from "react";
import { observer, inject } from "mobx-react";
// UI Framework
import {
	Form,
	Button as AntButton,
	Input as AntdInput,
	Row,
	Col,
	Icon,
	Upload,
	Select,
	Divider,
	Typography,
} from "antd";
import { FormComponentProps } from "antd/lib/form";
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
import DatePicker from "react-datepicker2";
// Shared components
import Button from "components/Button";
import Input from "components/Input";
//Local Components
import Main from "screens/Dashboard/components/Main";
// Utilities and hooks
import moment from "moment";
import jMoment from "moment-jalaali";
import cs from "classnames";
import { get, omit } from "lodash";
import UserStore from "state/user";
import useUser from "hooks/useUser";
import SecurityQuestionsService from "services/http/endpoints/security-questions";
// Services
import showNotify from "helpers/notify";
import { readToken } from "helpers/token";
import { getBase64 } from "helpers/index";
import { normalizeNumbers } from "helpers/persian";
import { checkNationalId } from "helpers/validators";
// Styles
import styles from "./index.module.scss";
import inputStyles from "components/Input/index.module.scss";

const { Dragger } = Upload;
const { Option } = Select;
const { Title } = Typography;

interface IProps extends FormComponentProps {
	onSubmit: () => void;
	user?: UserStore;
}

const EditInfo: React.FC<IProps> = props => {
	const { updateProfile, getUserInfo } = useUser();
	const [imageUrl, setImageUrl] = React.useState();
	const [loading, setLoading] = React.useState(false);
	const { getFieldDecorator, setFieldsValue } = props.form;
	const user = props.user ? props.user.getUser : {};
	const [questions, setQuestions] = React.useState([] as Account.SecurityQuestion[]);
	const userVerified = get(user, ["profile", "profileStatus"]) === "VERIFIED";

	const fetchData = async (params?: any) => {
		try {
			const result = await SecurityQuestionsService.search();
			setLoading(false);
			setQuestions(result.result);
		} catch (error) {
			setLoading(false);
		}
	};

	// get Questions
	React.useEffect(() => {
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();

		props.form.validateFields(async (err, values) => {
			if (!err) {
				try {
					setLoading(true);
					values.birthdayTimestamp = jMoment(values.birthdayTimestamp).valueOf();
					// @ts-ignore
					props.user.updateProfile(omit(values, "profileImage", "nationCartImage"));
					await updateProfile(values);
					await getUserInfo();
					props.onSubmit();
					setLoading(false);
				} catch (error) {
					setLoading(false);
				}
			}
		});
	}

	/**
	 * Validate iranian national id if possible
	 * @param callback
	 */
	function handleNidValidation(_: any, value: string, callback: any) {
		if (!value) {
			callback("لطفا کد ملی را وارد کنید");
		} else if (value && !checkNationalId.validate(value)) {
			callback("لطفا کد ملی را صحیح وارد کنید");
		} else {
			callback();
		}
	}

	function handleUpload(info: any, field: string) {
		if (info.file.status === "uploading") {
			setLoading(true);
			return;
		}
		if (info.file.status === "done") {
			const { data } = info.file.response;
			getBase64(info.file.originFileObj, image => {
				setLoading(false);
				setFieldsValue({ [field]: { id: data } });
				setImageUrl({ ...imageUrl, [field]: image });
			});
		}
	}

	function beforeUpload(file: { type: string; size: number }) {
		const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
		if (!isJpgOrPng) {
			showNotify({
				text: "You can only upload JPG/PNG file!",
			});
		}
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isLt2M) {
			showNotify({
				text: "Image must smaller than 2MB!",
			});
		}
		return isJpgOrPng && isLt2M;
	}

	const uploadButton = (
		<div>
			<Icon type={loading ? "loading" : "plus"} />
			<div className="ant-upload-text">بارگذاری</div>
		</div>
	);

	return (
		<Main title="ویرایش اطلاعات حساب کاربر">
			<div className="card">
				<div className={styles["edit-info-form"]}>
					<Form onSubmit={handleSubmit}>
						<Row gutter={24}>
							{get(user, ["profile", "shouldAskQuestion1"]) && (
								<Col span={24}>
									<Title level={3}>سوالات امنیتی</Title>
									{[1, 2, 3].map(
										(level, key) =>
											get(user, ["profile", `shouldAskQuestion${level}`]) && (
												<div key={key}>
													<Form.Item label={`سوال امنیتی ${level}`}>
														{getFieldDecorator("questionId" + level, {
															rules: [
																{
																	required: true,
																	transform: value => (value ? value.trim() : value),
																	message: "لطفا سوال امنیتی را انتخاب نمایید",
																},
															],
														})(
															<select style={{ width: "100%" }}>
																<option>سوالی را انتخاب کنید</option>
																{questions
																	.filter(q => q.level === level)
																	.map((question, key) => (
																		<option key={key} value={question.id}>
																			{question.body}
																		</option>
																	))}
															</select>,
														)}
													</Form.Item>
													<Form.Item>
														{getFieldDecorator("securityAnswer" + level, {
															rules: [
																{
																	required: true,
																	transform: value => (value ? value.trim() : value),
																	message: "لطفا پاسخ خود را وارد نمایید",
																},
															],
														})(
															<AntdInput
																placeholder="پاسخ خود را وارد کنید"
																className={styles["register-form__password-input"]}
															/>,
														)}
													</Form.Item>
												</div>
											),
									)}
									<Divider />
								</Col>
							)}
							<Col span={24}>
								<Title level={3}>اطلاعات حساب</Title>
								<Form.Item label="نام">
									{getFieldDecorator("firstname", {
										initialValue: get(user, ["profile", "firstname"]),
										rules: [
											{
												required: true,
												transform: value => (value ? value.trim() : value),
												message: "لطفا نام خود را وارد نمایید",
											},
										],
									})(<Input type="text" disabled={userVerified} />)}
								</Form.Item>
								<Form.Item label="نام خانوادگی">
									{getFieldDecorator("lastname", {
										initialValue: get(user, ["profile", "lastname"]),
										rules: [
											{
												required: true,
												transform: value => (value ? value.trim() : value),
												message: "لطفا نام خانوادگی خود را وارد نمایید",
											},
										],
									})(<Input type="text" disabled={userVerified} />)}
								</Form.Item>
								<Form.Item label="جنسیت">
									{getFieldDecorator("gender", {
										initialValue: get(user, ["profile", "gender"]),
										rules: [
											{
												required: true,
												message: "لطفا جنسیت خود را انتخاب کنید",
											},
										],
									})(
										<Select disabled={userVerified}>
											<Option value="MAN">مرد</Option>
											<Option value="WOMAN">زن</Option>
										</Select>,
									)}
								</Form.Item>
								<Form.Item label="کدملی">
									{getFieldDecorator("nationCode", {
										initialValue: get(user, ["profile", "nationCode"]),
										normalize: normalizeNumbers,
										rules: [
											{
												transform: value => (value ? value.trim() : value),
											},
											{
												validator: handleNidValidation,
											},
										],
									})(<Input maxLength={10} disabled={userVerified} />)}
								</Form.Item>
								{get(user, "usernameType") === "EMAIL" && (
									<>
										<Form.Item label="شماره موبایل">
											{getFieldDecorator("mobile", {
												initialValue: get(user, ["profile", "mobile"]),
												normalize: normalizeNumbers,
												rules: [
													{
														min: 11,
														message: "لطفا شماره موبایل خود را صحیح وارد نمایید",
													},
													{
														transform: value => (value ? value.trim() : value),
													},
												],
											})(<Input maxLength={11} disabled={userVerified} />)}
										</Form.Item>
										<Form.Item label="ایمیل">
											{getFieldDecorator("username", {
												initialValue: get(user, ["username"]),
												rules: [
													{
														type: "email",
														message: "لطفا ایمیل خود را صحیح وارد نمایید",
													},
													{
														transform: value => (value ? value.trim() : value),
													},
												],
											})(<Input disabled />)}
										</Form.Item>
									</>
								)}
								<Form.Item label="تاریخ تولد">
									{getFieldDecorator("birthdayTimestamp", {
										initialValue: get(user, ["profile", "birthdayTimestamp"])
											? jMoment(get(user, ["profile", "birthdayTimestamp"]))
											: null,
										rules: [
											{
												required: true,
												message: "لطفا تاریخ تولد خود را وارد نمایید",
											},
										],
									})(
										<DatePicker
											isGregorian={false}
											timePicker={false}
											max={moment().endOf("day")}
											className={cs("ant-input", inputStyles.input)}
										/>,
									)}
								</Form.Item>
								{!get(props.user, ["aanLogin"]) && (
									<Form.Item label="بارگذاری تصویر کارت ملی">
										{getFieldDecorator("nationCartImage", {
											initialValue: get(user, ["profile", "nationCartImage"]),
										})(
											<Dragger
												name="file"
												listType="picture-card"
												className="avatar-uploader"
												showUploadList={false}
												disabled={userVerified}
												action="/api/shop/general/v1/upload-file"
												beforeUpload={beforeUpload}
												onChange={info => handleUpload(info, "nationCartImage")}
												headers={{ Authorization: `Bearer ${readToken()}` }}
											>
												{get(imageUrl, "nationCartImage") ? (
													<img
														src={imageUrl.nationCartImage}
														alt="avatar"
														style={{ width: "100%", padding: 10 }}
													/>
												) : (
													uploadButton
												)}
											</Dragger>,
										)}
									</Form.Item>
								)}
								{!get(props.user, ["aanLogin"]) && (
									<Form.Item label="بارگذاری عکس پروفایل">
										{getFieldDecorator("profileImage", {
											initialValue: get(user, ["profile", "profileImage"]),
										})(
											<Dragger
												name="file"
												listType="picture-card"
												className="avatar-uploader"
												showUploadList={false}
												action="/api/shop/general/v1/upload-file"
												beforeUpload={beforeUpload}
												onChange={info => handleUpload(info, "profileImage")}
												headers={{ Authorization: `Bearer ${readToken()}` }}
											>
												{get(imageUrl, "profileImage") ? (
													<img
														src={imageUrl.profileImage}
														alt="avatar"
														style={{ width: "100%", padding: 10 }}
													/>
												) : (
													uploadButton
												)}
											</Dragger>,
										)}
									</Form.Item>
								)}
							</Col>
						</Row>
						<Form.Item className="button-center">
							<AntButton type="link" onClick={props.onSubmit}>
								بازگشت
							</AntButton>
							<Button htmlType="submit" primary loading={loading}>
								تایید و ویرایش
							</Button>
						</Form.Item>
					</Form>
				</div>
			</div>
		</Main>
	);
};
export default Form.create<IProps>({})(inject("user")(observer(EditInfo)));
